// src/features/notifications/context/NotificationProvider.jsx
//
// Single context owning ALL /api/tickets/since polling for the authenticated session.
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { fetchTicketsSince } from '../../../api/ismApi';
import {
  fetchNotificationHistory,
  fetchNotificationState,
  markAllNotificationsRead,
  markNotificationsRead,
  updateNotificationSettings,
} from '../../../api/notificationApi';
import { STORAGE_KEYS } from '../../../config/env';
import { logger } from '../../../shared/utils/logger';
import { loadMasterData } from '../../tickets/services/masterDataService';
import { createTicketMappingContext, mapTicketResponse } from '../../tickets/services/ticketMapper';
import {
  HISTORY_LIMIT,
  HIDDEN_RESYNC_THRESHOLD_MS,
  POLL_INTERVAL_MS,
  TOAST_AGGREGATE_THRESHOLD,
  TOAST_AUTO_DISMISS_MS,
} from '../constants';
import { createAggregateToast, createTicketToast, toHistoryEntry } from '../utils/notificationFactory';
import { createSeenIdsState, markSeenMany, partitionUnseen } from '../utils/seenIds';

const NotificationContext = createContext(null);
let activePollController = null;

const EMPTY_MASTER_DATA = { categories: [], statuses: [], users: [], categoryPicMaps: [] };

function historyEntry(item, mappingContext) {
  const candidate = item.raw_ticket_doc
    || (item.ticket && typeof item.ticket === 'object' ? item.ticket : item);
  const ticketId = item.ticket_ref_id || item.ticket_id || candidate._id || item.id || item._id;
  const ticketNo = item.request_no || item.ticket_no || candidate.request_no || ticketId;
  const hasResolvedTicketShape = Boolean(candidate.typeMeta && candidate.title);
  const hasClassificationData = Boolean(
    candidate.ai_suggested_category_ref_id
      || candidate.category_ref_id
      || candidate.category_name
      || candidate.categoryName
      || candidate.category
      || candidate.priority
      || candidate.priority_name
      || candidate.priority_value,
  );
  const mappedTicket = hasResolvedTicketShape
    ? candidate
    : mappingContext && hasClassificationData
      ? mapTicketResponse(candidate, mappingContext)
      : null;
  const rawCategory = item.category_name || item.categoryName || item.category;
  const rawPriority = item.priority ?? item.priority_name ?? item.priority_value;
  if (!mappedTicket && (!rawCategory || !rawPriority)) {
    logger.warn('Notification payload is missing ticket category/priority fields:', ticketId);
  }
  const ticketRef = {
    ...(mappedTicket || candidate),
    _id: ticketId,
    id: ticketNo,
    title: item.subject || candidate.subject || mappedTicket?.title || '(no subject)',
    category: mappedTicket?.category || rawCategory || 'unclassified',
    priority: mappedTicket?.priority || rawPriority || 'unclassified',
    created: item.ready_at || item.received_at || candidate.created,
  };
  return {
    id: ticketId,
    isRead: Boolean(item.is_read),
    ticketNo,
    subject: ticketRef.title,
    category: ticketRef.category,
    priority: ticketRef.priority,
    typeMeta: mappedTicket?.typeMeta || item.typeMeta || null,
    receivedAt: item.ready_at || item.received_at,
    ticketRef,
    _raw: item,
  };
}

function mergeHistory(current, incoming) {
  const byId = new Map(incoming.map(entry => [entry.id, entry]));
  current.forEach(entry => {
    if (!byId.has(entry.id)) byId.set(entry.id, entry);
  });
  return [...byId.values()].slice(0, HISTORY_LIMIT);
}

function readLegacyDisabled() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.notiEnabled);
    if (raw == null) return false;
    return { exists: true, disabled: raw === 'false' || JSON.parse(raw) === false };
  } catch {
    return { exists: false, disabled: false };
  }
}

export function NotificationProvider({ children }) {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [history, setHistory] = useState([]);
  const [unread, setUnread] = useState(0);
  const [historyError, setHistoryError] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [newTicketIds, setNewTicketIds] = useState([]);
  const [ticketsVersion, setTicketsVersion] = useState(0);
  const [resyncSignal, setResyncSignal] = useState(0);

  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const cursorRef = useRef(null);
  const initializedRef = useRef(false);
  const lastServerTimeRef = useRef(null);
  const initialBaselineRef = useRef(null);
  // seenIds is keyed by the RAW API doc's `id` field, which is the canonical
  // Mongo ObjectId (`serialize_document` renames `_id` -> `id` server-side).
  // This is deliberately NOT the same `id` used for table-row identity: after
  // mapping, `mapTicketResponse` re-purposes `.id` as the display request_no
  // and moves the canonical ObjectId to `._id`. Deduplicating "have we seen
  // this ticket" on the canonical id (before mapping) is robust even if
  // mapping/master-data lookups are incomplete. The exposed newTicketIds use
  // the mapped display ID to match RequestTable row keys.
  const seenIdsRef = useRef(createSeenIdsState());
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const hiddenAtRef = useRef(null);
  const mountedRef = useRef(true);
  const mappingContextRef = useRef(createTicketMappingContext(EMPTY_MASTER_DATA));
  const toastTimeoutsRef = useRef(new Map());
  const readQueueRef = useRef(new Set());
  const readSnapshotsRef = useRef(new Map());
  const readTimerRef = useRef(null);
  const historyCursorRef = useRef(null);
  const historyRequestRef = useRef(null);
  const pollBackoffRef = useRef(POLL_INTERVAL_MS);
  const nextPollAtRef = useRef(0);

  useEffect(() => {
    let active = true;
    loadMasterData().then(data => {
      if (!active) return;
      mappingContextRef.current = createTicketMappingContext(data);
      setHistory(current => current.map(entry => {
        if (!entry._raw) return entry;
        const remapped = historyEntry({ ...entry._raw, is_read: entry.isRead }, mappingContextRef.current);
        return { ...remapped, isRead: entry.isRead };
      }));
    }).catch(() => {
      // Best-effort: mapTicketResponse tolerates empty lookup tables (it
      // falls back to "Unknown"/default category/status labels), so a
      // failed master-data load degrades display quality but never blocks
      // polling or dashboard refresh signals.
    });
    return () => { active = false; };
  }, []);

  const dismissToast = useCallback((id) => {
    const timeoutId = toastTimeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      toastTimeoutsRef.current.delete(id);
    }
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((toast) => {
    setToasts(prev => [...prev, toast]);
    const timeoutId = setTimeout(() => dismissToast(toast.id), TOAST_AUTO_DISMISS_MS);
    toastTimeoutsRef.current.set(toast.id, timeoutId);
  }, [dismissToast]);

  const refreshServerState = useCallback(async ({ replaceHistory = false } = {}) => {
    const state = await fetchNotificationState();
    enabledRef.current = Boolean(state.enabled);
    setEnabled(enabledRef.current);
    if (state.unread_count != null || state.unread != null) {
      setUnread(Number(state.unread_count ?? state.unread) || 0);
    }
    if (replaceHistory) {
      const items = state.items || state.history || [];
        const entries = items.map(item => historyEntry(item, mappingContextRef.current));
      setHistory(mergeHistory([], entries));
      setHasMoreHistory(Boolean(state.has_more));
      const oldest = items[items.length - 1];
      historyCursorRef.current = oldest
        ? { beforeReadyAt: oldest.ready_at, beforeId: oldest.ticket_ref_id || oldest.ticket_id || oldest.id || oldest._id }
        : null;
      markSeenMany(seenIdsRef.current, items.map(item => item.id));
    }
    if (state.server_time) {
      lastServerTimeRef.current = state.server_time;
      if (replaceHistory) {
        cursorRef.current = { ts: state.server_time, afterId: null };
        initialBaselineRef.current = state.server_time;
      }
    }
    return state;
  }, []);

  const toggle = useCallback(async () => {
    if (settingsSaving) return;
    const previous = enabledRef.current;
    const next = !previous;
    enabledRef.current = next;
    setEnabled(next);
    setSettingsSaving(true);
    try {
      await updateNotificationSettings(next);
    } catch (error) {
      enabledRef.current = previous;
      setEnabled(previous);
      addToast({
        id: `settings-error-${Date.now()}`,
        kind: 'error',
        severity: 'error',
        message: 'Notification settings could not be saved. Try again.',
      });
      logger.warn('Notification setting update failed.', error);
    } finally {
      setSettingsSaving(false);
    }
  }, [addToast, settingsSaving]);

  const markAllRead = useCallback(async () => {
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = null;
    const pendingReadIds = [...readSnapshotsRef.current.entries()]
      .filter(([, wasRead]) => !wasRead)
      .map(([id]) => id);
    const previousHistory = history.map(entry => (
      pendingReadIds.includes(entry.id) ? { ...entry, isRead: false } : entry
    ));
    const previousUnread = unread + pendingReadIds.length;
    readQueueRef.current.clear();
    readSnapshotsRef.current.clear();
    setUnread(0);
    setHistory(prev => prev.map(entry => ({ ...entry, isRead: true })));
    try {
      const result = await markAllNotificationsRead();
      if (result?.unread_count != null) setUnread(Number(result.unread_count) || 0);
    } catch (error) {
      logger.warn('Could not persist mark-all-read.', error);
      setHistory(previousHistory);
      setUnread(previousUnread);
      addToast({
        id: `read-all-error-${Date.now()}`,
        kind: 'error',
        severity: 'error',
        message: 'Could not save notification read state. Try again.',
      });
    }
  }, [addToast, history, unread]);

  const flushReadQueue = useCallback(async () => {
    if (readQueueRef.current.size === 0) return;
    const ids = [...readQueueRef.current].slice(0, 100);
    ids.forEach(id => readQueueRef.current.delete(id));
    let result;
    let failure;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        result = await markNotificationsRead(ids);
        failure = null;
        break;
      } catch (error) {
        failure = error;
      }
    }
    if (failure) {
      const rollbackIds = new Set(ids);
      let rollbackCount = 0;
      setHistory(prev => prev.map(entry => {
        if (!rollbackIds.has(entry.id)) return entry;
        const wasRead = readSnapshotsRef.current.get(entry.id) ?? false;
        if (!wasRead) rollbackCount += 1;
        return { ...entry, isRead: wasRead };
      }));
      setUnread(value => value + rollbackCount);
      ids.forEach(id => readSnapshotsRef.current.delete(id));
      addToast({
        id: `read-error-${Date.now()}`,
        kind: 'error',
        severity: 'error',
        message: 'Could not save notification read state. Try again.',
      });
      return;
    }
    ids.forEach(id => readSnapshotsRef.current.delete(id));
    if (result?.unread_count != null) setUnread(Number(result.unread_count) || 0);
  }, [addToast]);

  const markTicketRead = useCallback((ticketId) => {
    if (!ticketId) return;
    const target = history.find(entry => entry.id === ticketId && !entry.isRead);
    if (!target) return;
    readSnapshotsRef.current.set(ticketId, target.isRead);
    setHistory(prev => prev.map(entry => {
      if (entry.id !== ticketId || entry.isRead) return entry;
      return { ...entry, isRead: true };
    }));
    setUnread(value => Math.max(0, value - 1));
    readQueueRef.current.add(ticketId);
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(() => {
      readTimerRef.current = null;
      flushReadQueue();
    }, 1000);
  }, [flushReadQueue, history]);

  const loadMoreHistory = useCallback(async () => {
    if (historyLoading || !hasMoreHistory || !historyCursorRef.current) return;
    setHistoryLoading(true);
    setHistoryError(null);
    const controller = new AbortController();
    historyRequestRef.current?.abort();
    historyRequestRef.current = controller;
    try {
      const page = await fetchNotificationHistory({
        ...historyCursorRef.current,
        limit: 20,
        signal: controller.signal,
      });
      const items = page?.items || page?.history || [];
      const entries = items.map(item => historyEntry(item, mappingContextRef.current));
      setHistory(current => mergeHistory(current, entries));
      setHasMoreHistory(Boolean(page?.has_more));
      const oldest = items[items.length - 1];
      historyCursorRef.current = oldest
        ? { beforeReadyAt: oldest.ready_at, beforeId: oldest.ticket_ref_id || oldest.ticket_id || oldest.id || oldest._id }
        : null;
    } catch (error) {
      if (!controller.signal.aborted) setHistoryError(error?.message || 'Could not load older notifications.');
    } finally {
      if (historyRequestRef.current === controller) historyRequestRef.current = null;
      if (!controller.signal.aborted) setHistoryLoading(false);
    }
  }, [hasMoreHistory, historyLoading]);

  // A ticket the user can no longer see (e.g. reassigned away after the toast
  // was shown) resolves to 404 on the detail route. The detail page calls
  // this so a dead entry doesn't linger in the history list.
  const removeFromHistory = useCallback((id) => {
    setHistory(prev => prev.filter(entry => entry.id !== id && entry.ticketRef?._id !== id));
  }, []);

  const retryInitialLoad = useCallback(async () => {
    setLoading(true);
    setHistoryError(null);
    try {
      await refreshServerState({ replaceHistory: true });
    } catch (error) {
      setHistoryError(error?.message || 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [refreshServerState]);

  const markSeen = useCallback((ids) => {
    markSeenMany(seenIdsRef.current, ids);
  }, []);

  const poll = useCallback(async ({ includeState = false } = {}) => {
    if (!initializedRef.current || !cursorRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (inFlightRef.current || activePollController) return;

    inFlightRef.current = true;
    const controller = new AbortController();
    activePollController = controller;
    abortRef.current = controller;
    const collected = [];
    let completed = false;
    let serverUnreadCount = null;
    const initialBaseline = initialBaselineRef.current;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { ts, afterId } = cursorRef.current;
        // eslint-disable-next-line no-await-in-loop
        const { status, headers, data } = await fetchTicketsSince({
          cursor: ts,
          afterId,
          includeState,
          signal: controller.signal,
        });

        if (status === 204) {
          const nextCursor = headers.get('x-next-cursor');
          cursorRef.current = { ts: nextCursor || ts, afterId: null };
          lastServerTimeRef.current = nextCursor || lastServerTimeRef.current;
          completed = true;
          break;
        }

        const unseenRaw = partitionUnseen(data.tickets, seenIdsRef.current, 'id');
        markSeenMany(seenIdsRef.current, unseenRaw.map(ticket => ticket.id));

        if (data.unread_count != null) serverUnreadCount = Number(data.unread_count) || 0;

        if (unseenRaw.length > 0) {
          const mapped = unseenRaw.map(doc => mapTicketResponse(doc, mappingContextRef.current));
          collected.push(...unseenRaw.map((doc, index) => ({ doc, ticket: mapped[index] })));
        }

        cursorRef.current = { ts: data.next_cursor, afterId: data.after_id || null };
        lastServerTimeRef.current = data.next_cursor || lastServerTimeRef.current;
        if (!data.has_more) {
          completed = true;
          break;
        }
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && !controller.signal.aborted) {
        // Keep the cursor unchanged on failure so the next tick re-covers
        // the same window instead of silently skipping missed tickets.
        logger.warn('Notification poll failed; the next tick will retry.', error);
        pollBackoffRef.current = Math.min(Math.max(POLL_INTERVAL_MS, pollBackoffRef.current * 2), 300000);
        nextPollAtRef.current = Date.now() + pollBackoffRef.current;
      }
    } finally {
      inFlightRef.current = false;
      if (activePollController === controller) activePollController = null;
      abortRef.current = null;
    }

    if (completed) {
      pollBackoffRef.current = POLL_INTERVAL_MS;
      nextPollAtRef.current = 0;
    }
    if (serverUnreadCount != null) setUnread(serverUnreadCount);
    if (completed) initialBaselineRef.current = null;
    if (!mountedRef.current) return;

    if (collected.length === 0) return;

    setNewTicketIds(collected.map(({ ticket }) => ticket.id));
    setTicketsVersion(value => value + 1);

    const notifyable = initialBaseline
      ? collected.filter(({ doc }) => new Date(doc.ready_at).getTime() > new Date(initialBaseline).getTime())
      : collected;
    if (notifyable.length === 0) return;

    const notifyableTickets = notifyable.map(({ ticket }) => ticket);
    const newestFirst = [...notifyableTickets].reverse();
    const entries = [...notifyable].reverse().map(({ doc, ticket }) => historyEntry({
      ...doc,
      request_no: doc.request_no || ticket.id,
      ticket,
      raw_ticket_doc: doc,
    }, mappingContextRef.current));
    setHistory(prev => mergeHistory(prev, entries));
    if (serverUnreadCount == null) {
      const unreadIncoming = notifyable.filter(({ doc }) => doc.is_read !== true).length;
      if (unreadIncoming) setUnread(prev => prev + unreadIncoming);
    }

    // The preference disables alert toasts only. New tickets still belong in
    // history and must increase the unread badge while notifications are Off.
    if (!enabledRef.current) return;

    if (notifyable.length > TOAST_AGGREGATE_THRESHOLD) {
      addToast(createAggregateToast(notifyable.length));
    } else {
      newestFirst.forEach(ticket => addToast(createTicketToast(ticket)));
    }
  }, [addToast]);

  useEffect(() => {
    mountedRef.current = true;
    let active = true;
    const initializationController = new AbortController();

    async function initialize() {
      const legacy = readLegacyDisabled();
      if (legacy.exists) {
        try {
          if (legacy.disabled) await updateNotificationSettings(false);
          localStorage.removeItem(STORAGE_KEYS.notiEnabled);
        } catch (error) {
          logger.warn('Legacy notification preference migration failed.', error);
        }
      }
      try {
        const state = await fetchNotificationState({ signal: initializationController.signal });
        if (!active) return;
        setEnabled(Boolean(state.enabled));
        enabledRef.current = Boolean(state.enabled);
        setUnread(Number(state.unread_count ?? state.unread) || 0);
        const items = state.items || state.history || [];
        setHistory(items.map(item => historyEntry(item, mappingContextRef.current)));
        setHasMoreHistory(Boolean(state.has_more));
        setHistoryError(null);
        const oldest = items[items.length - 1];
        historyCursorRef.current = oldest
          ? { beforeReadyAt: oldest.ready_at, beforeId: oldest.ticket_ref_id || oldest.ticket_id || oldest.id || oldest._id }
          : null;
        seenIdsRef.current = createSeenIdsState();
        markSeenMany(seenIdsRef.current, items.map(item => item.id));
        const baseline = state.server_time || new Date().toISOString();
        cursorRef.current = { ts: baseline, afterId: null };
        lastServerTimeRef.current = baseline;
        initialBaselineRef.current = baseline;
      } catch (error) {
        if (initializationController.signal.aborted) return;
        logger.warn('Could not load persisted notification state.', error);
        setHistoryError('Could not load notifications.');
        const fallback = new Date().toISOString();
        cursorRef.current = { ts: fallback, afterId: null };
        lastServerTimeRef.current = fallback;
        initialBaselineRef.current = fallback;
      } finally {
        if (active) {
          initializedRef.current = true;
          setLoading(false);
          poll({ includeState: true });
        }
      }
    }

    initialize();
    const intervalId = setInterval(() => {
      if (initializedRef.current && Date.now() >= nextPollAtRef.current) poll();
    }, POLL_INTERVAL_MS);

    function resyncThenPoll() {
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      const hiddenDurationMs = hiddenAt ? Date.now() - hiddenAt : 0;
      if (hiddenDurationMs > HIDDEN_RESYNC_THRESHOLD_MS) {
        setResyncSignal(value => value + 1);
      }
      if (initializedRef.current) poll({ includeState: true });
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        resyncThenPoll();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', resyncThenPoll);

    return () => {
      mountedRef.current = false;
      active = false;
      initializationController.abort();
      initializedRef.current = false;
      clearInterval(intervalId);
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
      historyRequestRef.current?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', resyncThenPoll);
      abortRef.current?.abort();
      toastTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      toastTimeoutsRef.current.clear();
    };
  }, [poll, refreshServerState]);

  const value = useMemo(() => ({
    enabled,
    loading,
    settingsSaving,
    toggle,
    toasts,
    addToast,
    dismissToast,
    history,
    unread,
    markAllRead,
    markTicketRead,
    refreshNotifications: refreshServerState,
    removeFromHistory,
    historyError,
    historyLoading,
    hasMoreHistory,
    loadMoreHistory,
    retryInitialLoad,
    newTicketIds,
    ticketsVersion,
    resyncSignal,
    markSeen,
  }), [
    enabled, loading, settingsSaving, toggle, toasts, addToast, dismissToast, history, unread, markAllRead, markTicketRead,
    refreshServerState,
    removeFromHistory, historyError, historyLoading, hasMoreHistory, loadMoreHistory, retryInitialLoad,
    newTicketIds, ticketsVersion, resyncSignal, markSeen,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
